import axios from "axios";
import toast from "react-hot-toast";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Helper to check and set logout flag (shared across all services)
const checkAndSetLogoutFlag = (): boolean => {
  const flag = sessionStorage.getItem('__is_logging_out__');
  if (flag === 'true') {
    return true; // Already logging out
  }
  sessionStorage.setItem('__is_logging_out__', 'true');
  return false; // First time, proceed with logout
};

export interface LoginResponse {
  user: {
    id: string;
    name?: string;
    username: string;
    email: string;
    is_admin: boolean;
    is_active: boolean;
    role?: "superadmin" | "admin" | "staff";
    created_at?: string;
    updated_at?: string;
  };
  token: string;
}

export interface User {
  id: string;
  name?: string;
  username: string;
  email: string;
  is_admin: boolean;
  is_active: boolean;
  role?: "superadmin" | "admin" | "staff";
  created_at?: string;
  updated_at?: string;
}

class AuthService {
  private api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });
  
  constructor() {
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("authToken");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const url = (error.config?.url || "").toLowerCase();
        // Do NOT redirect on 401 for the login request itself
        const isLoginCall = url.includes("/api/login");
        if (status === 401 && !isLoginCall) {
          // Only handle if configService hasn't already handled it
          // This prevents duplicate error messages
          if (!checkAndSetLogoutFlag()) {
            // Check if we're already on the login page
            const isOnLoginPage = window.location.pathname === '/login';
            if (isOnLoginPage) {
              // Already on login page, just clear the flag and return
              sessionStorage.removeItem('__is_logging_out__');
              return Promise.reject(error);
            }
            
            const message = error.response?.data?.message || "Session expired. Please login again.";
            const reason = error.response?.data?.reason;
            
            // Show appropriate toast message (only once, using unique ID to prevent duplicates)
            if (reason === 'token_invalidated') {
              toast.error(message, { duration: 4000, id: 'token-invalidated-global' });
              // Longer delay for token invalidation so user can read the message
              localStorage.removeItem("authToken");
              setTimeout(() => {
                sessionStorage.removeItem('__is_logging_out__'); // Clear flag before redirect
                window.location.href = "/login";
              }, 3500);
            } else {
              toast.error(message, { duration: 3000, id: 'session-expired-global' });
              localStorage.removeItem("authToken");
              setTimeout(() => {
                sessionStorage.removeItem('__is_logging_out__'); // Clear flag before redirect
                window.location.href = "/login";
              }, 2500);
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }
  
  setAuthToken(token: string) {
    this.api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }

  async login(username: string, password: string): Promise<LoginResponse> {
    try {
      const response = await this.api.post("/api/login", {
        username,
        password,
      });
      // Clear logout flag on successful login
      sessionStorage.removeItem('__is_logging_out__');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Login failed");
      }
      throw new Error("Login failed");
    }
  }

  async validateToken(token: string): Promise<User> {
    try {
      // Add timeout to prevent hanging
      const response = await Promise.race([
        this.api.get("/api/validate-token", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Request timeout")), 10000)
        ),
      ]) as any;
      return response.data;
    } catch (error) {
      // Clear token on any error to prevent stuck state
      localStorage.removeItem("authToken");
      throw new Error("Invalid token");
    }
  }
  
  async getProfile(): Promise<User> {
    const response = await this.api.get("/api/profile");
    return response.data;
  }
  
  async updateProfile(name: string): Promise<{ message: string; user: User }> {
    const response = await this.api.put("/api/profile", { name });
    return response.data;
  }
  
  async changePassword(payload: { currentPassword: string; newPassword: string; confirmPassword: string }): Promise<{ message: string }> {
    const response = await this.api.post("/api/profile/change-password", payload);
    return response.data;
  }
}

export const authService = new AuthService();
